import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MotivationPost from './MotivationPost';

const POSTS_PER_PAGE = 20;

// Title case helper
const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Sort functions
const sortFunctions = {
    confidenceHighToLow: (a, b) => (b.rating ?? 0) - (a.rating ?? 0),
    confidenceLowToHigh: (a, b) => (a.rating ?? 0) - (b.rating ?? 0),
    newest: (a, b) => new Date(b.time) - new Date(a.time),
    oldest: (a, b) => new Date(a.time) - new Date(b.time)
};

// Custom dropdown component
function SortDropdown({ value, onChange, options }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                }}
            >
                {options.find(opt => opt.value === value)?.label || 'Sort By'}
                <KeyboardArrowDownIcon style={{ fontSize: 16, transform: isOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 100,
                    minWidth: '160px',
                    overflow: 'hidden'
                }}>
                    {options.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => {
                                onChange(option.value);
                                setIsOpen(false);
                            }}
                            style={{
                                display: 'block',
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '12px',
                                textAlign: 'left',
                                color: option.value === value ? '#2563EB' : '#374151',
                                backgroundColor: option.value === value ? '#EFF6FF' : 'transparent',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const MotivationPostsDisplay = ({
    posts,
    onClose,
    motivationType = 'Motivation',
    subtitle,
    autoScroll = false,
    id = 'motivation-posts',
    query = null,
    valueColors = null   // optional (value) => ({ bg, text } | null) for value chips/tags
}) => {
    const [displayCount, setDisplayCount] = useState(POSTS_PER_PAGE);
    const [sortMethod, setSortMethod] = useState('confidenceHighToLow');

    const sortOptions = [
        { value: 'confidenceHighToLow', label: 'Confidence (High to Low)' },
        { value: 'confidenceLowToHigh', label: 'Confidence (Low to High)' },
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' }
    ];

    useEffect(() => {
        if (autoScroll && posts.length > 0) {
            const postsSection = document.getElementById(id);
            const mainContent = document.querySelector('.company-main-content');
            if (postsSection && mainContent) {
                const offsetTop = postsSection.offsetTop;
                mainContent.scrollTo({
                    top: offsetTop - 20,
                    behavior: 'smooth'
                });
            }
        }
    }, [posts, id, autoScroll]);

    if (!posts || posts.length === 0) return null;

    // Group posts by value for summary (with title case)
    const valueCounts = posts.reduce((acc, post) => {
        const values = Array.isArray(post.value) ? post.value : [post.value];
        values.forEach(v => {
            const titleCased = toTitleCase(v);
            acc[titleCased] = (acc[titleCased] || 0) + 1;
        });
        return acc;
    }, {});

    const sortedPosts = [...posts].sort(sortFunctions[sortMethod]);
    const displayedPosts = sortedPosts.slice(0, displayCount);
    const hasMorePosts = sortedPosts.length > displayCount;

    return (
        <div id={id} style={{
            backgroundColor: '#F7F7F9',
            borderRadius: '10px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '16px'
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '20px',
                        fontWeight: 600,
                        color: '#1a1a1a'
                    }}>
                        {motivationType}
                    </h3>
                    <p style={{
                        margin: '4px 0 0 0',
                        fontSize: '13px',
                        color: '#6B7280'
                    }}>
                        {posts.length} posts {subtitle ? `· ${subtitle}` : ''}
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <SortDropdown
                        value={sortMethod}
                        onChange={setSortMethod}
                        options={sortOptions}
                    />
                    {onClose && (
                        <button
                            onClick={onClose}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                backgroundColor: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: '#6B7280'
                            }}
                        >
                            <CloseIcon style={{ fontSize: 18 }} />
                        </button>
                    )}
                </div>
            </div>

            {/* Value breakdown summary */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #E5E7EB'
            }}>
                {Object.entries(valueCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([value, count]) => {
                        const colors = valueColors ? valueColors(value) : null;
                        return (
                            <span
                                key={value}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '4px 10px',
                                    fontSize: '11px',
                                    fontWeight: colors ? 600 : 500,
                                    color: colors ? colors.text : '#374151',
                                    backgroundColor: colors ? colors.bg : 'white',
                                    border: colors ? 'none' : '1px solid #E5E7EB',
                                    borderRadius: '16px'
                                }}
                            >
                                {value}: {count}
                            </span>
                        );
                    })
                }
            </div>

            {/* Posts list */}
            <div>
                {displayedPosts.map((post, index) => (
                    <MotivationPost
                        key={post.id || index}
                        post={post}
                        query={query}
                        motivationType={motivationType}
                        valueColors={valueColors}
                    />
                ))}
            </div>

            {/* Load more */}
            {hasMorePosts && (
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <button
                        onClick={() => setDisplayCount(prev => prev + POSTS_PER_PAGE)}
                        style={{
                            padding: '10px 24px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#2563EB',
                            backgroundColor: 'white',
                            border: '1px solid #2563EB',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        View More ({sortedPosts.length - displayCount} remaining)
                    </button>
                </div>
            )}
        </div>
    );
};

export default MotivationPostsDisplay;
